'use client'

import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import { forwardRef, useState, useImperativeHandle, useRef, useEffect, useId } from 'react'
import { validateEmail } from 'src/_helpers/validate'
import Typography from 'src/components/design-system/Typography'

export const CustomInput = forwardRef(
  (
    {
      label,
      name,
      type = 'text',
      required = false,
      placeholder = '',
      defaultValue = '',
      onChange,
      onFocus,
      onBlur,
      className = '',
      classNameContainer = '',
      color = 'dark-blue',
      labelShow = true,
    },
    ref,
  ) => {
    const translateEntry = useTranslateEntry()
    const [value, setValue] = useState(defaultValue)
    const [isValid, setIsValid] = useState(true)
    const [errorText, setErrorText] = useState('')
    const [touched, setTouched] = useState(false)
    const inputRef = useRef(null)
    const errorId = useId()

    const validate = () => {
      if (!required && !value) return true

      let valid = true
      let message = ''

      if (required && !value) {
        valid = false
        message = translateEntry('T_FIELD_REQUIRED')
      } else if (type === 'email' && value) {
        if (!email || !validateEmail(email)) {
          valid = false
          message = translateEntry('T_EMAIL_INVALID')
        }
      }

      setIsValid(valid)
      setErrorText(message)
      return valid
    }

    useImperativeHandle(ref, () => ({
      validate,
      getValue: () => value,
    }))

    useEffect(() => {
      if (touched && required) validate()
    }, [value, touched])

    useEffect(() => {
      setValue(defaultValue)
      setTouched(false)
      setIsValid(true)
      setErrorText('')
    }, [defaultValue])

    const handleChange = (e) => {
      setValue(e.target.value)
      setTouched(true)
      onChange?.({
        target: { name, value: e.target.value, isValid: required ? !!e.target.value : true },
      })
    }

    return (
      <div className={`w-full ${classNameContainer}`}>
        {label != '' && (
          <label htmlFor={name} className={`block pb-4 ${!labelShow && 'sr-only'}`}>
            <Typography variant="h4" component="span" color="text-basic-black">
              {label} {required && '*'}
            </Typography>
          </label>
        )}
        <input
          ref={inputRef}
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={() => onBlur?.({ isValid, errorText })}
          placeholder={placeholder}
          aria-invalid={!isValid}
          aria-describedby={!isValid ? errorId : undefined}
          className={`w-full border-b-1 ${color == 'journalDomain' ? 'border-b-basic-gray1 bg-basic-white h-44 text-basic-black h3 h3--light' : color == 'white' ? 'border-b-dark-blue-dark-blue3 bg-basic-white h-44 text-basic-black h3 h3--light' : 'border-b-dark-blue-dark-blue3 body-b3-normal text-basic-black px-12 bg-dark-blue-dark-blue6 h-44'} ${className}`}
        />

        {!isValid && errorText && (
          <p id={errorId} className="mt-4" role="alert">
            <Typography variant="body2" component="span" color="text-functional-error-red2">
              {errorText}
            </Typography>
          </p>
        )}
      </div>
    )
  },
)

CustomInput.displayName = 'CustomInput'
